import {
  List,
  ListItemIcon,
  ListItemText,
  Box,
  ListItemButton,
} from "@mui/material";
import {
  Settings,
  AreaChart,
  Payments,
  RecentActors,
  ReceiptLong,
  ViewList,
  Balance,
  AccountTree,
  ManageAccounts,
  AccountBalanceWalletOutlined,
  VpnKeyOutlined,
  PaidOutlined,
} from "@mui/icons-material";
import { SidebarProps } from "../utils/types";
import { useLocation, useNavigate } from "react-router";
import { useTheme } from "@mui/material";

const menuItems = [
  { text: "Транзакции", icon: <ReceiptLong />, path: "/transactions" },
  { text: "Балансы", icon: <Balance />, path: "/balances" },
  { text: "Мерчанты", icon: <RecentActors />, path: "/merchants" },
  { text: "Провайдеры", icon: <Payments />, path: "/providers" },
  { text: "Каскады", icon: <AccountTree />, path: "/cascades" },
  {
    text: "Методы оплаты",
    icon: <AccountBalanceWalletOutlined />,
    path: "/payment-methods",
  },
  { text: "Валюты", icon: <PaidOutlined />, path: "/currencies" },
  { text: "Ключи провайдеров", icon: <VpnKeyOutlined />, path: "/provider-keys" },
  { text: "Статистика", icon: <AreaChart />, path: "/stats" },
  { text: "Логи", icon: <ViewList />, path: "/logs" },
  { text: "Пользователи", icon: <ManageAccounts />, path: "/users" },
  { text: "Настройки", icon: <Settings />, path: "/settings" },
];

const Sidebar = ({ isMobile, open, setOpen }: SidebarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box
      height={isMobile ? "calc(100% - 8px)" : "100%"}
      flexDirection="column"
      justifyContent="space-between"
      position={isMobile ? "absolute" : "relative"}
      sx={{
        transition: "width 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
        backgroundColor:
          isMobile && open ? theme.palette.background.paper : "transparent",
        border: isMobile && open ? `1px solid ${theme.palette.divider}` : 0,
        borderRadius: isMobile && open ? "20px" : 0,
        boxShadow:
          isMobile && open ? "16px 0 48px rgba(8, 12, 24, 0.25)" : "none",
        overflow: "hidden",
        zIndex: 20,
        display: "flex",
        left: isMobile ? 4 : 0,
        top: isMobile ? 4 : 0,
        flexShrink: 0,
      }}
      width={open ? 264 : isMobile ? 0 : 72}
      onMouseEnter={!isMobile ? () => setOpen(true) : undefined}
      onMouseLeave={!isMobile ? () => setOpen(false) : undefined}
    >
      <List
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "6px",
          height: "100%",
          // На высоком экране пунктов меньше, чем места: без прокрутки и с
          // minHeight: auto список распирал бы контейнер. На низком —
          // прокручивается вместо того, чтобы обрезаться.
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: "14px",
          // Круглая кнопка меню висит fixed в левом нижнем углу (50px + 20px
          // отступа) — без запаса она перекрывала бы последний пункт.
          paddingBottom: isMobile ? "84px" : "14px",
          px: open ? 1.25 : 0.75,
        }}
      >
        {menuItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== "/stats" && location.pathname.startsWith(item.path));

          return (
            <ListItemButton
              key={item.path}
              selected={isActive}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setOpen(false);
              }}
              sx={{
                position: "relative",
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                width: "100%",
                // MUI задаёт ListItemButton flex: 1 1 auto — в колонке это
                // растягивает пункты на всю высоту контейнера, и на телефоне
                // девять пунктов расползались на весь экран.
                flex: "0 0 auto",
                minHeight: 50,
                borderRadius: "13px",
                px: 1.4,
                overflow: "hidden",
                "&.Mui-selected": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(99, 91, 255, 0.16)"
                      : "rgba(99, 91, 255, 0.10)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 12,
                    bottom: 12,
                    width: 3,
                    borderRadius: "0 4px 4px 0",
                    backgroundColor: theme.palette.primary.main,
                  },
                },
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(23, 32, 51, 0.05)",
                  color: isActive
                    ? theme.palette.primary.light
                    : theme.palette.text.primary,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: 0,
                  width: 40,
                  "& svg": { fontSize: 23 },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  whiteSpace: "nowrap",
                  opacity: open ? 1 : 0,
                  maxWidth: open ? 170 : 0,
                  overflow: "hidden",
                  transition: "max-width 0.3s ease, opacity 0.3s ease",
                  color: "inherit",
                  ml: open ? 1.25 : 0,
                  "& .MuiTypography-root": {
                    color: "inherit",
                    fontWeight: isActive ? 680 : 560,
                    fontSize: 14,
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
